import { Suspense, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '@clerk/react';
import { RedirectToSignIn } from '@clerk/react';
import { useLocation } from 'react-router-dom';

import { ToastBar } from '@client/toolkit';

import { AppBlocking } from './app-blocking';
import { AppError } from './app-error/app-error';
import { AppLoading } from './app-loading';
import { AppRoutes } from './app-routes/app-routes';

import { useAppDispatch, useAppSelector, useTenantSync } from '../hooks';
import { AppStatus, initApp, selectAppStatus } from '../redux';
import { PortfolioPaths } from '../pods/portfolio/portfolio.router';
import { ThemeProvider } from '@mui/material';
import theme from './theme';

export function App() {
  const { isLoaded, isSignedIn } = useAuth();
  const appStatus = useAppSelector(selectAppStatus);
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Onboarding route should bypass normal app init (user has no tenant yet)
  const isOnboardingRoute = location.pathname.startsWith(PortfolioPaths.Onboarding);

  // Sync tenant selection across browser tabs
  useTenantSync();

  useEffect(() => {
    // Only init app if Clerk is loaded and user is signed in
    // Skip for onboarding routes — user has no tenant yet, OnboardingApp handles auth independently
    if (isLoaded && isSignedIn && !isOnboardingRoute) {
      dispatch(initApp());
    }
  }, [dispatch, isLoaded, isSignedIn, isOnboardingRoute]);

  // Show loading while Clerk is loading
  if (!isLoaded) {
    return <AppLoading show />;
  }

  // Redirect to sign in if not authenticated
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<AppLoading show />}>
        <Toaster position="bottom-right">{(t) => <ToastBar toast={t} />}</Toaster>
        {(appStatus === AppStatus.READY || appStatus === AppStatus.BLOCKED || isOnboardingRoute) && <AppRoutes />}
        <AppBlocking />
        {!isOnboardingRoute && <AppLoading />}
        {!isOnboardingRoute && <AppError />}
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
